import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { Divider, Grid } from '@material-ui/core';
import enableIcon from '../../../assets/images/arrow-right.png';
import StringResourceManager, { GetString } from '../../StringResourceManager';
import { CategoryCountRequest } from '../../../Interfaces/Assets/CategoryCountRequest';
import {
  getCurrentIssuesForCategory,
  getCurrentIssueCountsByDateRange,
  resetAssetStatusesFetchError,
  getCurrentIssuePropertiesCSV,
} from '../../../store/Devices/AssetStatesActions';
import { TrendsFilter as TrendsFilterData } from '../../../Interfaces/Devices/Trends/TrendsFilter';
import { AssetStatePropertiesCountsByDateRange } from '../../../Interfaces/Devices/Trends/AssetStatePropertiesCountsByDateRange';
import { AssetInfo } from '../../../Interfaces/Assets/AssetInfo';
import { saveDeviceTrendsFilter } from '../../../store/UserSettings/UserSettingsActions';
import { ApiEnums } from '../../../Interfaces/ApiEnums';
import {
  isNullOrEmptyString,
  dateFormat,
  getDateRange,
} from '../../../utils/utils';
import { Button } from '@progress/kendo-react-buttons';
import TrendsAssetStatusesFilter from './TrendsAssetStatusesFilter';
import { isMatch } from '../../../utils/textMappings';
import { FetchError } from '../../../Interfaces/FetchError';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Error } from '@progress/kendo-react-labels';
import { LocaleInterface, locales } from '../../AlertsGrid/GridLocale';
import {
  CurrentIssuePropertiesCSV,
  FilteredCurrentIssuePropertiesCSV,
} from '../../../Interfaces/Devices/Trends/CurrentIssuePropertiesCSV';
import { CSVLink } from 'react-csv';
import DynamicComboBox from '../../DynamicComboBox';

const useStyles = makeStyles(theme =>
  createStyles({
    margin: {
      margin: theme.spacing(2),
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      height: '50px',
      margin: '20px',
      width: '50px',
    },
    disable: {
      backgroundColor: '#e9ecef',
    },
    enable: {
      backgroundColor: '#FFFFFF',
    },
    dropDownLabelGrid: {
      width: '200px',
    },
    runQueryButton: {
      height: 40,
      width: 200,
      marginTop: 10,
      marginLeft: 20,
      marginBottom: 10,
    },
    headerLabelGrid: {
      width: '100%',
      marginTop: 10,
      marginLeft: 10,
      marginBottom: 10,
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    dateRangeModal: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.shape.borderRadius,
      height: '500px',
    },
    dateRangeContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      margin: theme.spacing(2),
    },
    headerRowElement: {
      margin: theme.spacing(0.5),
    },
    actionButton: {
      backgroundColor: '#153654',
      width: '150px',
      color: '#fff',
      '&:hover': {
        background: '#153654',
        color: '#fff',
      },
    },
    toolbarButton: {
      height: 40,
      width: 200,
      marginTop: 10,
      marginLeft: 20,
      marginBottom: 10,
    },
  })
);

const TrendsFilter: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const status = GetString('STATUS');
  const START_DATE_ERRORMSG = GetString('START_DATE_ERRORMSG');
  const END_DATE_ERRORMSG = GetString('END_DATE_ERRORMSG');

  const currentIssueCategories: string[] = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.currentIssueCategories
  );

  const currentIssueValues: Map<CategoryCountRequest, string[]> = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.currentIssueValues
  );

  const deviceTrendsFilter: TrendsFilterData = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.deviceTrendsFilter
  );

  const assetStatePropertiesCountsByDateRange: AssetStatePropertiesCountsByDateRange =
    useSelector(
      (state: ApplicationState) =>
        state.assetStatusesReducer.assetStatePropertiesCountsByDateRange
    );

  const isFetchingCurrentIssueValues: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.isFetchingCurrentIssueValues
  );

  const [valueFilterVisible, setValueFilterVisible] =
    React.useState<boolean>(false);

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.fetchError
  );

  const currentIssuePropertiesCSVData: CurrentIssuePropertiesCSV[] =
    useSelector(
      (state: ApplicationState) =>
        state.assetStatusesReducer.currentIssuePropertiesCSVData
    );

  const [error, displayError] = React.useState(false);

  const [modalVisible, setModalVisible] = React.useState<boolean>(false);

  const [startDateRange, setStartDateRange] = React.useState<Date>(new Date());

  const [endDateRange, setEndDateRange] = React.useState<Date>(new Date());

  const [isStartDateRangeChange, setIsStartDateRangeChange] =
    React.useState<boolean>(false);

  const [isEndDateRangeChange, setIsEndDateRangeChange] =
    React.useState<boolean>(false);

  const [errorMsg, setErrorMsg] = React.useState<boolean>(false);

  const clearFetchError = () => {
    displayError(false);
    resetAssetStatusesFetchError(dispatch);
  };

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [consolidatedCSVData, setConsolidatedCSVData] = React.useState<
    FilteredCurrentIssuePropertiesCSV[]
  >([]);

  const ALL = GetString('ALL');

  const startDateLabel = GetString('START_DATE_LABEL');

  const endDateLabel = GetString('END_DATE_LABEL');

  const dateRangeOption = [
    GetString('TODAY'),
    GetString('YESTERDAY'),
    GetString('LAST_SEVEN_DAYS'),
    GetString('LAST_THIRTY_DAYS'),
    GetString('OTHER'),
  ];

  useEffect(() => {
    startDateRange.setDate(startDateRange.getDate() - 7);
  }, []);

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  React.useEffect(() => {
    processFetchCategoryCounts(deviceTrendsFilter.AssetInfo);
    if (!currentIssueCategories.includes(ALL))
      currentIssueCategories.unshift(ALL);

    if (
      deviceTrendsFilter.AssetInfo.Category !== '' &&
      !isOptionEqualToAll(deviceTrendsFilter.AssetInfo.Category)
    ) {
      setValueFilterVisible(true);
    }
  }, [currentIssueCategories]);

  React.useEffect(() => {
    updateDateRangeFilter(
      getDateRange(deviceTrendsFilter.DateRange),
      deviceTrendsFilter.DateRange
    );

    if (
      !isInvalidQuery() &&
      assetStatePropertiesCountsByDateRange.Counts.length == 0
    )
      getCurrentIssueCountsByDateRange(dispatch);
  }, []);

  const onChangeCurrentIssueCategory = (event: any) => {
    if (
      isOptionEqualToAll(deviceTrendsFilter.AssetInfo.Category) &&
      event.target.value == null
    )
      return;

    let newAssetInfo: AssetInfo = {
      Category: event.target.value,
      Name: deviceTrendsFilter.AssetInfo.Name,
      Value: '',
    };

    if (event.target.value == null) {
      newAssetInfo.Category = ALL;
      setValueFilterVisible(false);
    }

    updateAssetInfoFilter(newAssetInfo);

    if (event.target.index == 0) {
      setValueFilterVisible(false);
    } else if (event.target.index > 0) {
      setValueFilterVisible(true);
      processFetchCategoryCounts(newAssetInfo);
    }
  };

  const processFetchCategoryCounts = (assetInfo: AssetInfo) => {
    let valueExist: boolean = false;
    currentIssueValues.forEach(function (value: any, key: any, map: any) {
      if (JSON.stringify(key) == JSON.stringify(assetInfo)) {
        valueExist = true;
      }
    });

    if (!valueExist) {
      getCurrentIssuesForCategory(dispatch, assetInfo);
    }
  };

  const onChangeCurrentIssueValue = (event: any) => {
    if (
      (isOptionEqualToAll(deviceTrendsFilter.AssetInfo.Value) ||
        isNullOrEmptyString(deviceTrendsFilter.AssetInfo.Value)) &&
      isNullOrEmptyString(event.target.value)
    )
      return;

    updateAssetInfoFilter({
      Category: deviceTrendsFilter.AssetInfo.Category,
      Name: deviceTrendsFilter.AssetInfo.Name,
      Value: isNullOrEmptyString(event.target.value) ? ALL : event.target.value,
    });
  };

  const getDeviceTrendsData = (category: string, name: string) => {
    let assetInfo: AssetInfo = {
      Category: category,
      Name: name,
      Value: '',
    };

    let values: any = [];
    currentIssueValues.forEach(function (value: any, key: any, map: any) {
      if (key.Category == assetInfo.Category && key.Name == assetInfo.Name) {
        values = value;
      }
    });

    let filtered: string[] = values.filter(function (v: string) {
      return v != '';
    });

    if (filtered.length > 0) {
      filtered.unshift(ALL);
      if (
        deviceTrendsFilter.AssetInfo.Category != ALL &&
        deviceTrendsFilter.AssetInfo.Value == ''
      ) {
        let newAssetInfo: AssetInfo = {
          Category: deviceTrendsFilter.AssetInfo.Category,
          Name: deviceTrendsFilter.AssetInfo.Name,
          Value: ALL,
        };
        updateAssetInfoFilter(newAssetInfo);
      }
    }

    return filtered;
  };

  const updateAssetInfoFilter = (data: AssetInfo) => {
    let newFilter: TrendsFilterData = { ...deviceTrendsFilter };
    newFilter.AssetInfo = data;

    saveDeviceTrendsFilter(dispatch, newFilter);
  };

  const onDateRangeOptionChange = (event: any) => {
    if (event.target.index === ApiEnums.DateRangeOptions.Other) {
      setModalVisible(true);
      return;
    }

    updateDateRangeFilter(getDateRange(event.target.index), event.target.index);
  };

  const updateDateRangeFilter = (
    dateRange: [Date, Date] | undefined,
    dateRangeKey: any
  ) => {
    if (dateRange === undefined) {
      console.error('Invalid Trends date range selected');
      return;
    }

    let newFilter: TrendsFilterData = { ...deviceTrendsFilter };
    newFilter.StartDate = dateRange[0];
    newFilter.EndDate = dateRange[1];
    processSaveDeviceTrendsFilter(newFilter, dateRangeKey);
  };

  const processSaveDeviceTrendsFilter = (
    trendsFilterData: TrendsFilterData,
    selectedDateRangeOption: ApiEnums.DateRangeOptions
  ) => {
    trendsFilterData.DateRange = selectedDateRangeOption;
    saveDeviceTrendsFilter(dispatch, trendsFilterData);
  };

  const getDateRangeValue = () => {
    switch (deviceTrendsFilter.DateRange) {
      case ApiEnums.DateRangeOptions.Today:
        return setSelectedDateRangeOption([dateRangeOption[0]]);
      case ApiEnums.DateRangeOptions.Yesterday:
        return setSelectedDateRangeOption([dateRangeOption[1]]);
      case ApiEnums.DateRangeOptions.LastSevenDays:
        return setSelectedDateRangeOption([dateRangeOption[2]]);
      case ApiEnums.DateRangeOptions.LastThirtyDays:
        return setSelectedDateRangeOption([dateRangeOption[3]]);
      case ApiEnums.DateRangeOptions.Other:
        return setSelectedDateRangeOption([dateRangeOption[4]]);
      default:
        return setSelectedDateRangeOption([dateRangeOption[2]]);
    }
  };

  const isInvalidQuery = () => {
    if (isNullOrEmptyString(deviceTrendsFilter.AssetInfo.Category)) return true;
    if (
      !isOptionEqualToAll(deviceTrendsFilter.AssetInfo.Category) &&
      isNullOrEmptyString(deviceTrendsFilter.AssetInfo.Value)
    )
      return true;
    else return false;
  };

  const isOptionEqualToAll = (option: string) => {
    return isMatch('ALL', option);
  };

  const onRunQueryPressed = () => {
    getCurrentIssueCountsByDateRange(dispatch);
  };

  const handleClose = () => {
    setModalVisible(false);
  };

  const okButtonOnClick = () => {
    let newFilter: TrendsFilterData = { ...deviceTrendsFilter };
    startDateRange.setHours(0, 0, 0, 0);

    let endDate: Date = new Date(endDateRange);
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(0, 0, 0, 0);

    newFilter.StartDate = startDateRange;
    newFilter.EndDate = endDate;
    processSaveDeviceTrendsFilter(newFilter, ApiEnums.DateRangeOptions.Other);
    setModalVisible(false);
  };

  const cancelButtonOnClick = () => {
    setModalVisible(false);
  };

  const [selectedDateRangeOption, setSelectedDateRangeOption] = React.useState<
    string[]
  >([dateRangeOption[0]]);

  React.useEffect(() => {
    getDateRangeValue();
  }, [deviceTrendsFilter.DateRange]);

  const onOpen = () => {
    if (deviceTrendsFilter.DateRange === ApiEnums.DateRangeOptions.Other) {
      setSelectedDateRangeOption([dateRangeOption[4]]);
    }
  };

  React.useEffect(() => {
    if (startDateRange > endDateRange) setErrorMsg(true);
    else setErrorMsg(false);
  }, [startDateRange, endDateRange]);

  const [csvData, setCSVData] = React.useState<any>([]);

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const getCSVFileName = () => {
    return `Device_Issue_Trends_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    getCurrentIssuePropertiesCSV(dispatch);
  };

  const updateDateFormat = (
    obj: FilteredCurrentIssuePropertiesCSV
  ): FilteredCurrentIssuePropertiesCSV => {
    return {
      ...obj,
      CreatedOn: new Date(obj.CreatedOn).toLocaleString(),
      Date: new Date(obj.Date).toLocaleDateString(),
    };
  };
  
  const filteredCurrentIssuePropertiesCSVData = () => {
    var filteredFields = currentIssuePropertiesCSVData.map(
      ({
        Division,
        SiteNumber,
        NodeGroup,
        LaneNumber,
        Category,
        Model,
        Value,
        Description,
        FirmwareVersion,
        SerialNumber,
        RegionInfo,
        CreatedOn,
        Date,
        AgeDays,
        MachineName,
      }) => ({
        Division,
        SiteNumber,
        NodeGroup,
        LaneNumber,
        Category,
        Model,
        Value,
        Description,
        FirmwareVersion,
        SerialNumber,
        RegionInfo,
        MachineName,
        CreatedOn,
        Date,
        AgeDays,
      }),
    );

    return filteredFields.map(updateDateFormat);
  };

  useEffect(() => {
    if (currentIssuePropertiesCSVData.length > 0) {
      setConsolidatedCSVData(filteredCurrentIssuePropertiesCSVData());
    }
  }, [currentIssuePropertiesCSVData]);

  useEffect(() => {
    exportCSV();
  }, [consolidatedCSVData]);

  const exportCSV = () => {
    if (consolidatedCSVData?.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }

    getDateRangeValue();
  }, [selectedLanguage]);

  const categoryListNoDataRender = (
    element: React.ReactElement<HTMLDivElement>
  ) => {
    const noData = (
      <StringResourceManager
        variant="body1"
        stringKey={'NO_CATEGORIES_FOUND'}
      />
    );

    return React.cloneElement(element, { ...element.props }, noData);
  };

  const issueListNoDataRender = (
    element: React.ReactElement<HTMLDivElement>
  ) => {
    const noData = (
      <StringResourceManager
        variant="body1"
        stringKey={'NO_ISSUES_FOUND_ON_THE_SELECTED_CATEGORY'}
      />
    );

    return React.cloneElement(element, { ...element.props }, noData);
  };

  return (
    <React.Fragment>
      <CSVLink
        data={consolidatedCSVData}
        ref={csvLinkRef}
        filename={getCSVFileName() + '.csv'}
      />
      <div className={classes.headerLabelGrid}>
        <StringResourceManager
          variant="body1"
          style="dialogLabel"
          stringKey={'SELECT_ISSUE'}
          gutterBottom={true}
        />
      </div>
      <Grid container className={classes.margin}>
        <Grid item className={classes.dropDownLabelGrid}>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'CATEGORY'}
            gutterBottom={true}
          />
          <DynamicComboBox
            onChange={onChangeCurrentIssueCategory}
            data={currentIssueCategories}
            value={deviceTrendsFilter.AssetInfo.Category}
            placeholder={GetString('CATEGORY_PLACEHOLDER')}
            listNoDataRender={categoryListNoDataRender}
          />
        </Grid>
        {valueFilterVisible ? (
          <Grid className={classes.iconGrid}>
            <img src={enableIcon} className={classes.arrowIcon} />
          </Grid>
        ) : null}
        {valueFilterVisible ? (
          <Grid item className={classes.dropDownLabelGrid}>
            <StringResourceManager
              variant="body1"
              style="dialogLabel"
              stringKey={'INPUT_ISSUE'}
              gutterBottom={true}
            />
            <DynamicComboBox
              onChange={onChangeCurrentIssueValue}
              data={getDeviceTrendsData(
                deviceTrendsFilter.AssetInfo.Category,
                deviceTrendsFilter.AssetInfo.Name
              )}
              value={deviceTrendsFilter.AssetInfo.Value}
              loading={isFetchingCurrentIssueValues}
              listNoDataRender={issueListNoDataRender}
            />
          </Grid>
        ) : null}
        <Grid className={classes.iconGrid}>
          <img src={enableIcon} className={classes.arrowIcon} />
        </Grid>
        <Grid item className={classes.dropDownLabelGrid}>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'DATE_RANGE'}
            gutterBottom={true}
          />
          <DynamicComboBox
            data={dateRangeOption}
            onChange={onDateRangeOptionChange}
            value={selectedDateRangeOption}
            onOpen={onOpen}
          />
        </Grid>
      </Grid>
      <Divider component="div" role="presentation" />
      <div className={classes.headerLabelGrid}>
        <StringResourceManager
          variant="body1"
          style="dialogLabel"
          stringKey={'SELECT_FILTERS_OPTIONAL'}
          gutterBottom={true}
        />
      </div>
      <TrendsAssetStatusesFilter deviceTrendsFilter={deviceTrendsFilter} />
      <Divider component="div" role="presentation" />
      <div style={{ flexDirection: 'row' }}>
        <Button
          onClick={onRunQueryPressed}
          type="submit"
          className={classes.runQueryButton}
          disabled={isInvalidQuery()}
        >
          <StringResourceManager stringKey="RUN_QUERY" />
        </Button>
        <Button
          type="submit"
          className={classes.toolbarButton}
          onClick={processExportCSV}
        >
          {GetString('EXPORT_TO_CSV')}
        </Button>
      </div>
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {error && (
            <Notification
              className={classes.error}
              type={{
                style: 'error',
                icon: true,
              }}
              closable={true}
              onClose={() => clearFetchError()}
            >
              <StringResourceManager stringKey={fetchError.Message} />
              {`(${status}: ${fetchError.StatusCode})`}
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
      {modalVisible ? (
        <Dialog
          title={<StringResourceManager stringKey={'DATE_RANGE'} />}
          onClose={handleClose}
          className={classes.dateRangeModal}
        >
          <div className={classes.dateRangeContainer}>
            <div className={classes.headerRowElement}>
              <DatePicker
                value={startDateRange}
                onChange={event => {
                  setStartDateRange(event.value!);
                  setIsStartDateRangeChange(true);
                  setErrorMsg(false);
                }}
                label={startDateLabel}
              />
              {errorMsg && (
                <Error direction={'start'}>{START_DATE_ERRORMSG}</Error>
              )}
            </div>
            <div className={classes.headerRowElement}>
              <DatePicker
                value={endDateRange}
                label={endDateLabel}
                onChange={event => {
                  setEndDateRange(event.value!);
                  setIsEndDateRangeChange(true);
                  setErrorMsg(false);
                }}
              />
              {errorMsg && (
                <Error direction={'start'}>{END_DATE_ERRORMSG}</Error>
              )}
            </div>
          </div>
          <DialogActionsBar layout={'stretched'}>
            <Button
              onClick={okButtonOnClick}
              className={classes.actionButton}
              disabled={startDateRange > endDateRange ? true : false}
            >
              <StringResourceManager stringKey={'OK_BUTTON'} />
            </Button>
            <Button
              onClick={cancelButtonOnClick}
              className={classes.actionButton}
            >
              <StringResourceManager stringKey={'CANCEL_BUTTON'} />
            </Button>
          </DialogActionsBar>
        </Dialog>
      ) : null}
    </React.Fragment>
  );
};

export default TrendsFilter;
